import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import { debounceTime, map, Subject, Subscription, takeUntil } from 'rxjs';
import { CarFilterOptions } from 'src/app/models/car-filter-options.model';
import { Car } from 'src/app/models/car.model';
import { KeyText } from 'src/app/models/key-text.model';
import { CarService } from 'src/app/services/car/car.service';
import { KeyTextService } from 'src/app/services/key-text/key-text.service';
import { TypeOfReport } from 'src/app/shared/enums/type-of-report.enum';
import {take} from "rxjs/operators";
import {ToleranceViewerDto} from "../../../models/viewmodels/tolerance-viewer-dto.model";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ToleranceDialogComponent} from "../../tolerance-dialog/tolerance-dialog.component";
import {ProjectService} from "../../../services/project/project.service";
import {TranslateModule} from "@ngx-translate/core";
import {LoaderComponent} from "../../../shared/components/loader/loader.component";
import {MatIcon} from "@angular/material/icon";
import {NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {MatCard, MatCardTitle} from "@angular/material/card";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import {MatFabButton} from '@angular/material/button';

@Component({
  selector: 'colimo-car-report-configuration',
  templateUrl: './car-report-configuration.component.html',
  styleUrls: ['./car-report-configuration.component.scss'],
  imports: [
    TranslateModule,
    LoaderComponent,
    MatIcon,
    MatTooltip,
    NgClass,
    MatOption,
    MatSelect,
    MatCard,
    MatFormField,
    ReactiveFormsModule,
    MatSlideToggle,
    FormsModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatCardTitle,
    MatLabel,
    MatInput,
    MatFabButton
  ]
})
export class CarReportConfigurationComponent implements OnInit, OnDestroy {
  @Input() public filterOptions: CarFilterOptions;
  @Input() public isCarReportExportLayout: boolean;
  @Input() public carBodyStyleLoaded: boolean;
  @Input() public selectedTypeOfReport: string;
  @Input() public preSelectedLstaNr: number;
  @Input() public preSelectedCarId: string;
  @Input() public currentCar: Car;
  @Output() public initiateSearch = new EventEmitter<void>();
  @Output() public hideComponentsForCarsSearch = new EventEmitter<void>();
  @Output() public displayTypeOfReport = new EventEmitter<string>();
  @Output() public exportCarReport = new EventEmitter<void>();
  @Output() public usageOfUntouchedFilters = new EventEmitter<boolean>();

  private static readonly DEBOUNCE_TIME_IN_MILLISECONDS = 1000;

  public carId = new FormControl<string>('');
  public vehicleTypes: string[];
  public charactersCount: number;
  public isHidden: boolean;
  public isLoading: boolean;
  public locations: KeyText[];
  public selectedLocation: KeyText;
  public carsForLocation: Car[];
  public isSearchingCar: boolean;
  public typesOfReport: string[];

  private stop$: Subject<boolean> = new Subject();
  private searchCarsForLocationSubscription: Subscription;

  private useUntouchedFilters: boolean = true;

  // This subject is needed as a wrapper in which the debounceTime operator can be used to delay the search
  private debouncedSearchSubject: Subject<string> = new Subject();

  private toleranceDialogRef?: MatDialogRef<ToleranceDialogComponent>;

  constructor(private keyTextService: KeyTextService,
              private carService: CarService,
              public projectService: ProjectService,
              private matDialog: MatDialog) {}

  public ngOnInit(): void {
    this.isLoading = true;
    this.charactersCount = 2;
    this.isHidden = true;
    this.isSearchingCar = false;
    this.typesOfReport = [TypeOfReport.DE, TypeOfReport.DLABCH, TypeOfReport.DLABCH_AVERAGE_PLOT];
    this.initializeDebouncedSearch();
    this.loadLocations();
  }

  public ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.unsubscribe();
  }

  public searchCarsForLocation(): void {
    if (this.searchCarsForLocationSubscription) {
      this.searchCarsForLocationSubscription.unsubscribe();
    }
    // search should only performed if at least defined number of characters are typed in
    if (this.carId.value && this.carId.value.length >= this.charactersCount) {
      this.isSearchingCar = true;
      this.filterOptions.carId = this.carId.value;
      this.hideComponentsForCarsSearch.emit();
      // We need to pass any value that invokes the subject to start the search
      this.debouncedSearchSubject.next('START_PROCESSING');
    }
  }

  public onCarSelected(car: Car): void {
    this.hideSearchResults();
    this.filterOptions.carId = car.vehicleId;
    // the first time a car gets selected get additional filteroptions from the car
    if (this.useUntouchedFilters) {
      this.filterOptions.tolerancesOn = car.toleranceOn !== false;
    }
    this.initiateSearch.emit();
  }

  private loadPreselectedCar(car: Car): void {
    this.hideSearchResults();
    this.filterOptions.carId = car.vehicleId;
    this.filterOptions.tolerancesOn = null
    this.initiateSearch.emit();
  }

  public hideSearchResults(): void {
    this.isHidden = true;
  }

  public loadLocations(): void {
    this.keyTextService.getLocations().subscribe((locations: KeyText[]) => {
      this.locations = locations.sort((a: KeyText, b: KeyText) => a.text.localeCompare(b.text));
      if (this.locations.length !== 0) {
        if (this.preSelectedLstaNr && this.preSelectedCarId) {
          this.selectedLocation = this.locations.find((location) => location.key === String(this.preSelectedLstaNr));
          this.filterOptions.locationId = this.preSelectedLstaNr;
          this.carId.setValue(this.preSelectedCarId);
          this.loadPreselectedCar({ vehicleId: this.preSelectedCarId } as Car);
        } else {
          this.selectedLocation = this.locations[0];
          this.filterOptions.locationId = Number(this.locations[0].key);
        }
      }
      this.isLoading = false;
    });
  }

  public onLocationChanged(changedLocationKeyEvent: MatSelectChange): void {
    const changedLocationKey = changedLocationKeyEvent.value.key;
    this.filterOptions.locationId = changedLocationKey;
    this.searchCarsForLocation();
    this.setUsageOfUntouchedFilters(true);
  }

  public onFilterOptionsChanged(event: any): void {
    this.initiateSearch.emit();
    // only the tolerance switch should be considered for filter option change (currently bumpers and in case of plot lab/ch switch are ignored)
    if (event && event.source && ('tolerancesOn' === event.source.name)) {
      this.setUsageOfUntouchedFilters(false);
    }
  }

  public onTypeOfReportChanged(): void {
    this.displayTypeOfReport.emit(this.selectedTypeOfReport);
    this.setUsageOfUntouchedFilters(false);
  }

  public exportReport(): void {
    // close tolerance dialog when exporting if its open -- check later to add it at the end of the pdf print
    if (this.toleranceDialogRef) {
      this.toleranceDialogRef.close();
    }

    this.exportCarReport.emit();
  }

  private initializeDebouncedSearch(): void {
    this.debouncedSearchSubject
      .pipe(
        takeUntil(this.stop$),
        debounceTime(CarReportConfigurationComponent.DEBOUNCE_TIME_IN_MILLISECONDS),
        map(() => {
          this.carService.searchCar(this.filterOptions).subscribe((cars: Car[]) => {
            this.carsForLocation = cars;
            this.isSearchingCar = false;
            this.isHidden = false;
          });
        }),
      )
      .subscribe();
  }

  private setUsageOfUntouchedFilters(value: boolean): void {
    this.useUntouchedFilters = value;
    this.usageOfUntouchedFilters.emit(value);
  }

  // #487495 - Tolerance viewer
  public showTolerances(): void {
    if (this.currentCar) {
      this.projectService.getTolerancesByBatch(String(this.currentCar.batchId)).pipe(take(1)).subscribe({
        next: (response: ToleranceViewerDto) => {
          this.openToleranceDialog(response);
        },
        error: (error) => {
          // this.showError(error);
        }
      });
    }
  }

  private openToleranceDialog(toleranceData: ToleranceViewerDto): void {
    // If already open, just bring it to front / update data (optional)
    if (this.toleranceDialogRef) {
      // optional: update dialog data if you want “latest click wins”
      this.toleranceDialogRef.componentInstance.data.toleranceData = toleranceData;
      this.toleranceDialogRef.updatePosition(); // harmless nudge; optional
      return;
    }

    this.toleranceDialogRef = this.matDialog.open(ToleranceDialogComponent, {
      data: {toleranceData},
      disableClose: true,
      hasBackdrop: false
    });

    this.toleranceDialogRef.afterClosed().subscribe(() => {
      this.toleranceDialogRef = undefined;
    });
  }

}
